import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Video from "../../video/video2.mp4";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from "../../firebase/firebase-config";

import { AuthContext } from "../../context/AuthContext";

import {
  HeroBg,
  HeroBtnWrapper,
  HeroContainer,
  HeroContent,
  HeroH1,
  HeroP,
  VideoBg,
} from "./HeroElements";

import {
  Form,
  FormButton,
  FormH1,
  FormInput,
  FormLabel,
  Text,
  ToggleButton,
  GenderSelect,
  RoleToggleWrap,
  RoleToggleBtn,
} from "./SigninElements";

import LiveCounters from "./LiveCounters";

const normalizeRole = (role) => {
  if (!role) return null;
  if (role === "driver") return "chauffeur";
  return role;
};

const extractUserAndRole = (responseData, fallbackUserType) => {
  const user =
    responseData?.user ||
    responseData?.chauffeur ||
    responseData?.client ||
    null;

  const backendRole =
    responseData?.role ||
    user?.role ||
    fallbackUserType ||
    null;

  return {
    user,
    role: normalizeRole(backendRole),
  };
};

const HeroSection = () => {
  const { isAuthenticated, login, checkAuth } = useContext(AuthContext);

  const [isLogin, setIsLogin] = useState(true);
  const [userType, setUserType] = useState("client");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");
  const [referredBy, setReferredBy] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pendingVerificationEmail, setPendingVerificationEmail] = useState("");
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  const [showReviewButton, setShowReviewButton] = useState(false);

  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL || "http://localhost:5002";
  const PUBLIC_APP_URL = process.env.REACT_APP_PUBLIC_URL || "https://dieli.fr";

  const endpoints = useMemo(
    () => ({
      client: {
        login: `${API}/api/clients/login`,
        signup: `${API}/api/clients/signup`,
        google: `${API}/api/clients/auth/google`,
        resendVerification: `${API}/api/clients/resend-verification`,
      },
      driver: {
        login: `${API}/api/auth/login-driver`,
        signup: `${API}/api/auth/register-chauffeur`,
        google: `${API}/api/auth/auth/google`,
        resendVerification: `${API}/api/chauffeurs/resend-driver-verification`,
      },
    }),
    [API]
  );

  const forgotPasswordUrl = useMemo(() => {
    return userType === "client"
      ? `${PUBLIC_APP_URL}/forgot-password/client`
      : `${PUBLIC_APP_URL}/forgot-password/driver`;
  }, [PUBLIC_APP_URL, userType]);

  useEffect(() => {
    checkAuth?.();
  }, [checkAuth]);

  useEffect(() => {
    AOS.init({
      duration: 900,
      once: true,
      mirror: false,
    });
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [isAuthenticated, isLogin]);

  useEffect(() => {
    let timer;

    if (isAuthenticated) {
      setShowReviewButton(false);

      timer = setTimeout(() => {
        setShowReviewButton(true);
      }, 7000);
    } else {
      setShowReviewButton(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated]);

  const handleToggleAuthMode = (e) => {
    e.stopPropagation();
    setError("");
    setSuccess("");
    setPendingVerificationEmail("");
    setIsLogin((prev) => !prev);
  };

  const handleUserType = (type) => {
    setError("");
    setSuccess("");
    setPendingVerificationEmail("");
    setUserType(type);
  };

  const clearForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setGender("");
    setReferredBy("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError("");
    setSuccess("");
    setPendingVerificationEmail("");
    setIsSubmitting(true);

    try {
      if (!isLogin && password !== confirmPassword) {
        setError("Les mots de passe ne correspondent pas !");
        return;
      }

      if (isLogin) {
        const response = await axios.post(endpoints[userType].login, {
          email,
          password,
        });

        const token = response.data?.token;
        if (!token) throw new Error("Token manquant dans la réponse.");

        const { user, role } = extractUserAndRole(response.data, userType);

        login(token, user, role);
        clearForm();
      } else {
        const payload =
          userType === "client"
            ? { firstName, lastName, email, password, phone: phoneNumber }
            : {
                gender,
                firstName,
                lastName,
                email,
                password,
                phoneNumber,
                referredBy: referredBy?.trim() || null,
              };

        const response = await axios.post(endpoints[userType].signup, payload);

        const token = response.data?.token;

        if (token) {
          const { user, role } = extractUserAndRole(response.data, userType);
          login(token, user, role);
        }

        setSuccess(
          response.data?.message ||
            `Bienvenue ${firstName || ""} 🎉 Inscription réussie !`
        );

        clearForm();
      }
    } catch (err) {
      const data = err?.response?.data;

      if (data?.code === "EMAIL_NOT_VERIFIED") {
        setError(
          data.message ||
            "Ton email n’a pas encore été confirmé. Vérifie ta boîte mail."
        );
        setPendingVerificationEmail(data.email || email);
        return;
      }

      setError(data?.message || err.message || "Erreur inconnue");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (isSubmitting) return;

    setError("");
    setSuccess("");
    setPendingVerificationEmail("");
    setIsSubmitting(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();

      const response = await axios.post(endpoints[userType].google, {
        idToken,
        referredBy: userType === "driver" ? referredBy?.trim() || null : null,
      });

      const token = response.data?.token;
      if (!token) throw new Error("Token manquant dans la réponse Google.");

      const { user, role } = extractUserAndRole(response.data, userType);

      login(token, user, role);
      clearForm();
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Google Auth: erreur"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerificationEmail) return;

    try {
      setIsResendingVerification(true);
      setError("");
      setSuccess("");

      const endpoint =
        userType === "client"
          ? endpoints.client.resendVerification
          : endpoints.driver.resendVerification;

      const response = await axios.post(endpoint, {
        email: pendingVerificationEmail,
      });

      setSuccess(
        response.data?.message || "Un nouvel email de validation a été envoyé ✅"
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Impossible de renvoyer l’email de validation."
      );
    } finally {
      setIsResendingVerification(false);
    }
  };

  const AuthedNotice = () => (
  <div
    style={{
      width: "min(720px, 100%)",
      margin: "14px auto 0 auto",
      marginTop: "204px",
      padding: "14px 1px",
      borderRadius: 16,
      textAlign: "center",
      background: "rgba(0,0,0,0.40)",
      border: "1px solid rgba(255,255,255,0.14)",
      boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      color: "rgba(255,255,255,0.92)",
    }}
  >
    {!showReviewButton ? (
      <>
        <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 6 }}>
          Application bientôt disponible sur iOS et Android !
        </div>
        <div
          style={{
            color: "rgba(255,255,255,0.78)",
            lineHeight: 1.45,
            fontSize: 14,
          }}
        >
          Merci pour votre soutien 🙏 Nous vous tiendrons informé(e) de l’évolution.
          <br />
          Pour des retours / feedback nous reviendrons vers vous par email.
          <br />
          <br />
          Profitez dès maintenant des offres de parrainage exclusives pour les chauffeurs : 2 mois de commission à 0% à gagner !
        </div>
      </>
    ) : (
      <div>
        <div
          style={{
            fontWeight: 900,
            fontSize: 16,
            marginBottom: 10,
          }}
        >
          Merci pour votre confiance 🙏
        </div>

        <button
          type="button"
          onClick={() => navigate("/avis")}
          style={{
            minHeight: 48,
            padding: "0 18px",
            border: "none",
            borderRadius: 999,
            fontWeight: 900,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(0,103,114,0.96), rgba(212,175,55,0.88))",
            color: "#fff",
            boxShadow: "0 14px 30px rgba(0,0,0,0.34)",
          }}
        >
          Laisser un avis
        </button>
      </div>
    )}
  </div>
);

  return (
    <HeroContainer id="home">
      <HeroBg>
        <VideoBg autoPlay loop muted playsInline src={Video} type="video/mp4" />
      </HeroBg>

      <HeroContent>
        <HeroH1 data-aos="fade-up"> </HeroH1>

        <div data-aos="fade-up" data-aos-delay="100">
          <LiveCounters apiBaseUrl={API} />
        </div>

        {isAuthenticated && <AuthedNotice />}

        {!isAuthenticated && (
          <>
            <HeroP data-aos="fade-up" data-aos-delay="120">
              {isLogin ? "Connectez-vous à votre compte" : "Créez un nouveau compte"}
            </HeroP>

            <HeroBtnWrapper data-aos="zoom-in" data-aos-delay="180">
              <Form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
                <FormH1>{isLogin ? "Se connecter" : "Créer un compte"}</FormH1>

                <FormLabel>Je suis :</FormLabel>
                <RoleToggleWrap>
                  <RoleToggleBtn
                    type="button"
                    $active={userType === "client"}
                    onClick={() => handleUserType("client")}
                  >
                    Client
                  </RoleToggleBtn>
                  <RoleToggleBtn
                    type="button"
                    $active={userType === "driver"}
                    onClick={() => handleUserType("driver")}
                  >
                    Chauffeur
                  </RoleToggleBtn>
                </RoleToggleWrap>

                {!isLogin && userType === "driver" && (
                  <>
                    <FormLabel htmlFor="gender">Genre</FormLabel>
                    <GenderSelect
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="Mr">Mr</option>
                      <option value="Mme">Mme</option>
                    </GenderSelect>
                  </>
                )}

                {!isLogin && (
                  <>
                    <FormLabel htmlFor="firstName">Prénom</FormLabel>
                    <FormInput
                      type="text"
                      value={firstName}
                      required
                      onChange={(e) => setFirstName(e.target.value)}
                    />

                    <FormLabel htmlFor="lastName">Nom</FormLabel>
                    <FormInput
                      type="text"
                      value={lastName}
                      required
                      onChange={(e) => setLastName(e.target.value)}
                    />

                    <FormLabel htmlFor="phoneNumber">
                      Numéro de téléphone (ex: +33612345678)
                    </FormLabel>
                    <FormInput
                      type="tel"
                      value={phoneNumber}
                      required
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />

                    {userType === "driver" && (
                      <>
                        <FormLabel htmlFor="referredBy">Code parrainage (optionnel)</FormLabel>
                        <FormInput
                          type="text"
                          value={referredBy}
                          onChange={(e) => setReferredBy(e.target.value)}
                          placeholder="Ex: JELI-AB12CD34"
                        />
                      </>
                    )}
                  </>
                )}

                <FormLabel htmlFor="email">Email</FormLabel>
                <FormInput
                  type="email"
                  value={email}
                  required
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (pendingVerificationEmail) setPendingVerificationEmail("");
                  }}
                />

                <FormLabel htmlFor="password">Mot de passe</FormLabel>
                <FormInput
                  type="password"
                  value={password}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                />

                {!isLogin && (
                  <>
                    <FormLabel htmlFor="confirmPassword">Confirmer le mot de passe</FormLabel>
                    <FormInput
                      type="password"
                      value={confirmPassword}
                      required
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </>
                )}

                <FormButton type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? "Envoi..."
                    : isLogin
                    ? userType === "client"
                      ? "Se connecter (Client)"
                      : "Se connecter (Chauffeur)"
                    : userType === "client"
                    ? "S'inscrire (Client)"
                    : "S'inscrire (Chauffeur)"}
                </FormButton>

                <button
                  type="button"
                  onClick={handleGoogleAuth}
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    marginTop: 10,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.10)",
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: 900,
                    cursor: "pointer",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  }}
                >
                  Continuer avec Google
                </button>

                {isLogin && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "14px",
                    }}
                  >
                    <a
                      href={forgotPasswordUrl}
                      style={{
                        color: "#D4AF37",
                        fontWeight: 800,
                        fontSize: "14px",
                        textDecoration: "none",
                        textAlign: "center",
                      }}
                    >
                      Mot de passe oublié ?
                    </a>
                  </div>
                )}

                {success && <Text style={{ color: "#BBF7D0" }}>{success}</Text>}

                {error && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "14px 16px",
                      borderRadius: 14,
                      background: "rgba(239, 68, 68, 0.12)",
                      border: "1px solid rgba(239, 68, 68, 0.22)",
                      color: "#FCA5A5",
                    }}
                  >
                    <div style={{ fontWeight: 800, lineHeight: 1.5 }}>{error}</div>

                    {pendingVerificationEmail && (
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            color: "rgba(255,255,255,0.78)",
                            fontSize: 14,
                            lineHeight: 1.5,
                          }}
                        >
                          Vérifie aussi tes spams. Si tu n’as rien reçu, demande un nouvel email.
                        </div>

                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={isResendingVerification}
                          style={{
                            width: "100%",
                            minHeight: 46,
                            border: "none",
                            borderRadius: 999,
                            padding: "0 16px",
                            fontWeight: 900,
                            cursor: "pointer",
                            background:
                              "linear-gradient(135deg, rgba(0,103,114,0.96), rgba(212,175,55,0.88))",
                            color: "#fff",
                            opacity: isResendingVerification ? 0.7 : 1,
                          }}
                        >
                          {isResendingVerification
                            ? "Envoi..."
                            : "Renvoyer l’email de validation"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Form>

              <ToggleButton onClick={handleToggleAuthMode} type="button">
                {isLogin ? "Créer un compte" : "Déjà un compte ? Se connecter"}
              </ToggleButton>
            </HeroBtnWrapper>
          </>
        )}
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;